import { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import {
  makeStyles, tokens, Text, Button, Spinner, MessageBar, MessageBarBody,
  TabList, Tab,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { usePuedeVerActividad } from '../stores/authStore.js';
import {
  getActividadDeUsuario,
  type ActividadDeUsuario,
  type CuandoDeUsuario,
  type ProyectoDeUsuario,
} from '../api/staffActividad.js';
import { haceCuanto, fechaYHora, soloHora, duracionLegible } from '../lib/cuandoFue.js';

/**
 * Qué hizo una persona — SOLO staff Ecosoft.
 *
 * DOS LENTES, PORQUE SON DOS PREGUNTAS DISTINTAS y ninguna de las dos contesta la otra:
 *
 *  · **Sus proyectos** — "¿en qué quedó esto?". Cada proyecto con su estado: si armó la
 *    hoja, cuántos renglones tienen precio, cuántos análisis con IA generó. Es la vista
 *    del día a día, y es la que sirve para contestarle a alguien que pregunta por su obra.
 *  · **Paso a paso** — "¿qué le pasó a esta persona?". Lo mismo agrupado en RATOS de
 *    trabajo, en orden, saltando entre proyectos como saltó de verdad. Es la que sirve
 *    cuando algo salió mal: lo del primer cliente real —ocho intentos con el mismo rechazo
 *    en cuarenta minutos— SOLO se ve así. Repartido por proyecto no se nota, porque los
 *    ocho intentos son ocho renglones sueltos de un proyecto entre varios.
 *
 * Un rato se corta con media hora de silencio. Cuando un rato toca más de un proyecto se
 * dice arriba: ese salto es parte de la historia y es justo lo que la otra lente pierde.
 *
 * Arriba de las dos lentes van las DOS FECHAS —inicio de sesión y última actividad— que
 * antes eran una sola mal nombrada (ver `DosFechas`).
 */

const VENTANAS = [7, 30, 90, 365];

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-canvas)' },
  body: { padding: '20px 28px 64px', maxWidth: '1100px', margin: '0 auto' },
  head: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px' },
  small: { fontSize: tokens.fontSizeBase200, color: 'var(--text-secondary)' },
  barra: {
    display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap',
    padding: '12px 14px', margin: '14px 0',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  derecha: { marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' },

  // Las dos fechas, una al lado de la otra y con la misma pinta: se leen comparándolas.
  fechas: { display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '14px 0 6px' },
  fecha: {
    flex: '1 1 240px',
    padding: '10px 14px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  fechaTitulo: {
    display: 'block',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: 'var(--text-secondary)',
    marginBottom: '2px',
  },
  fechaValor: { display: 'block', fontWeight: 600, color: 'var(--text-primary)' },
  fechaDetalle: {
    display: 'block', fontSize: tokens.fontSizeBase200, color: 'var(--text-secondary)',
  },

  tablaWrap: {
    overflowX: 'auto',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  th: {
    textAlign: 'left', padding: '10px 12px', whiteSpace: 'nowrap',
    color: 'var(--text-secondary)', fontWeight: 600,
    borderBottom: '1px solid var(--border-subtle)',
  },
  td: {
    padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)',
    color: 'var(--text-primary)', verticalAlign: 'middle',
  },
  num: { fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' },
  vacio: { padding: '28px 16px', textAlign: 'center', color: 'var(--text-secondary)' },

  rato: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: '10px',
    overflow: 'hidden',
  },
  ratoCabeza: {
    display: 'flex', gap: '10px', alignItems: 'baseline', flexWrap: 'wrap',
    padding: '10px 14px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-subtle, transparent)',
  },
  ratoCuando: { fontWeight: 600, color: 'var(--text-primary)' },
  ratoSalto: { color: 'var(--warn, #d29922)', fontSize: tokens.fontSizeBase200 },
  pasos: { listStyle: 'none', margin: 0, padding: '6px 0' },
  paso: {
    display: 'grid',
    gridTemplateColumns: '52px 1fr auto',
    gap: '10px',
    alignItems: 'baseline',
    padding: '5px 14px',
    fontSize: tokens.fontSizeBase200,
  },
  pasoHora: { fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' },
  pasoProyecto: { color: 'var(--text-secondary)', fontSize: '11px', whiteSpace: 'nowrap' },
  detalle: {
    gridColumn: '2 / -1',
    fontFamily: 'var(--font-mono)',
    fontSize: '11px',
    color: 'var(--text-tertiary, var(--text-secondary))',
    wordBreak: 'break-word',
  },
});

/**
 * De cada evento, lo poco que aporta al relato. El metadata crudo trae identificadores y
 * números internos que llenan la pantalla sin decir nada; acá se eligen a mano los campos
 * que un humano sí quiere leer al reconstruir qué pasó.
 */
function detalleDelPaso(m: Record<string, unknown> | null): string | null {
  if (!m) return null;
  const partes: string[] = [];
  const txt = (k: string, etiqueta?: string) => {
    const v = m[k];
    if (v == null || v === '') return;
    partes.push(etiqueta ? `${etiqueta}: ${String(v)}` : String(v));
  };
  // POR DÓNDE ENTRÓ. Va primero porque es lo que explica una racha de entradas seguidas:
  // no es lo mismo alguien insistiendo con el botón de OPUS 27 que alguien reescribiendo
  // su contraseña en la web. Antes del 24/08 no se guardaba y no aparece.
  txt('origen', 'entró');
  txt('motivo', 'motivo');
  txt('sourceFile', 'archivo');
  txt('archivo', 'archivo');
  if (Array.isArray(m.archivos)) partes.push(m.archivos.join(', '));
  txt('conceptos', 'renglones');
  txt('conceptsTotal', 'renglones');
  txt('conceptsPriced', 'con precio');
  txt('conceptosOk', 'análisis');
  txt('counter', 'cupo');
  txt('limit', 'tope');
  txt('current', 'iba en');
  txt('newPlan', 'plan');
  txt('motivo');
  txt('error');
  if (typeof m.durationMs === 'number') partes.push(`tardó ${Math.round(m.durationMs / 1000)} s`);
  return partes.length > 0 ? partes.join(' · ') : null;
}

/**
 * Las dos fechas, separadas a propósito.
 *
 * Antes había una sola y decía "Entró", así que se leía como "estuvo aquí hasta entonces".
 * El 2026-08-25 se vio la contradicción: sesiones activas mostraba a una persona con
 * actividad hacía 9 minutos y esta pantalla la daba por conectada 15 horas antes. Las dos
 * decían la verdad — la sesión se renueva sola mientras alguien trabaja, así que no vuelve
 * a registrarse un inicio nuevo. La respuesta no es elegir una fecha: es mostrar las dos y
 * nombrarlas bien.
 */
function DosFechas({ cuando }: { cuando: CuandoDeUsuario }) {
  const styles = useStyles();
  return (
    <>
      <div className={styles.fechas}>
        <div className={styles.fecha}>
          <span className={styles.fechaTitulo}>Inició sesión</span>
          <span className={styles.fechaValor}>
            {cuando.inicioDeSesion ? haceCuanto(cuando.inicioDeSesion) : 'Sin registro'}
          </span>
          <span className={styles.fechaDetalle}>
            {cuando.inicioDeSesion
              ? `${fechaYHora(cuando.inicioDeSesion)} · última vez que entró con su cuenta`
              : 'No hay ninguna entrada registrada de esta persona.'}
          </span>
        </div>
        <div className={styles.fecha}>
          <span className={styles.fechaTitulo}>Última actividad</span>
          <span className={styles.fechaValor}>
            {cuando.ultimaActividad ? haceCuanto(cuando.ultimaActividad) : 'Sin registro'}
          </span>
          <span className={styles.fechaDetalle}>
            {cuando.ultimaActividad
              ? `${fechaYHora(cuando.ultimaActividad)}${
                  cuando.ultimaActividadQueHizo ? ` · ${cuando.ultimaActividadQueHizo}` : ''
                }`
              : 'Entró, pero todavía no hizo nada que quede registrado.'}
          </span>
        </div>
      </div>
      <Text className={styles.small} style={{ display: 'block', marginBottom: '8px' }}>
        No son lo mismo: mientras la persona trabaja, su sesión se mantiene sola y no
        necesita volver a entrar, así que el inicio de sesión puede quedar horas atrás
        aunque siga usando OPUS ai u OPUS ci. Aquí cuenta lo que dejó registrado; para saber quién
        está trabajando en este momento, entra a Sesiones activas.
      </Text>
    </>
  );
}

function FilaProyecto({ p }: { p: ProyectoDeUsuario }) {
  const styles = useStyles();
  return (
    <tr>
      <td className={styles.td}>
        <Link to={`/projects/${p.id}`}>{p.nombre}</Link>
        <div className={styles.small}>creado {haceCuanto(p.creado)}</div>
      </td>
      <td className={`${styles.td} ${styles.num}`}>{p.archivos}</td>
      <td className={styles.td}>{p.hojaSeLee}</td>
      <td className={`${styles.td} ${styles.num}`}>
        {p.conceptos == null ? '—' : `${p.conPrecio ?? 0} de ${p.conceptos}`}
      </td>
      <td className={`${styles.td} ${styles.num}`}>{p.apus || '—'}</td>
      <td className={`${styles.td} ${styles.num}`}>
        {p.ultimoMovimiento ? haceCuanto(p.ultimoMovimiento) : '—'}
      </td>
    </tr>
  );
}

export function StaffActividadUsuarioPage() {
  const styles = useStyles();
  // Ser staff no alcanza: esta pantalla la ven tres personas. Quién, lo decide el
  // servidor. Esto solo evita el viaje en balde — el portero de verdad está en los
  // dos endpoints, porque una pantalla escondida se abre escribiendo la dirección.
  const puedeVer = usePuedeVerActividad();
  const { userId } = useParams<{ userId: string }>();

  const [dias, setDias] = useState(30);
  const [lente, setLente] = useState<'proyectos' | 'pasos'>('proyectos');
  const [datos, setDatos] = useState<ActividadDeUsuario | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async (d: number) => {
    if (!userId) return;
    setCargando(true);
    try {
      setDatos(await getActividadDeUsuario(userId, d));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer la actividad de esta persona');
    } finally {
      setCargando(false);
    }
  }, [userId]);

  useEffect(() => {
    void cargar(dias);
  }, [cargar, dias]);

  if (!puedeVer) return <Navigate to="/" replace />;

  const quien = datos?.usuario.nombre ?? datos?.usuario.email ?? 'Cargando…';

  return (
    <div className={styles.page}>
      <AppTopBar />
      <div className={styles.body}>
        <Breadcrumbs
          items={[
            { label: 'Inicio', to: '/' },
            { label: 'Actividad (staff)', to: '/staff/actividad' },
            { label: quien },
          ]}
        />
        <div className={styles.head}>
          <Text size={600} weight="semibold">{quien}</Text>
          {datos?.usuario.nombre && (
            <Text className={styles.small}>{datos.usuario.email}</Text>
          )}
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: '14px' }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {datos && <DosFechas cuando={datos.cuando} />}

        <TabList
          selectedValue={lente}
          onTabSelect={(_, d) => setLente(d.value as 'proyectos' | 'pasos')}
        >
          <Tab value="proyectos">Sus proyectos</Tab>
          <Tab value="pasos">Paso a paso</Tab>
        </TabList>

        <div className={styles.barra}>
          <Text className={styles.small}>Últimos:</Text>
          {VENTANAS.map((d) => (
            <Button
              key={d}
              size="small"
              appearance={d === dias ? 'primary' : 'secondary'}
              onClick={() => setDias(d)}
            >
              {d === 365 ? '1 año' : `${d} días`}
            </Button>
          ))}
          <div className={styles.derecha}>
            <Button
              size="small"
              icon={<ArrowClockwiseRegular />}
              onClick={() => void cargar(dias)}
              disabled={cargando}
            >
              Actualizar
            </Button>
          </div>
        </div>

        {cargando && !datos ? (
          <Spinner label="Reconstruyendo lo que hizo…" />
        ) : lente === 'proyectos' ? (
          <>
            <Text className={styles.small} style={{ display: 'block', marginBottom: '8px' }}>
              Todos sus proyectos y en qué quedó cada uno. El estado sale del presupuesto más
              reciente de cada proyecto: si lo intentó varias veces, se muestra el último.
            </Text>
            <div className={styles.tablaWrap}>
              <table className={styles.tabla}>
                <thead>
                  <tr>
                    <th className={styles.th}>Proyecto</th>
                    <th className={styles.th}>Archivos</th>
                    <th className={styles.th}>Hoja</th>
                    <th className={styles.th}>Renglones con precio</th>
                    <th className={styles.th}>Análisis con IA</th>
                    <th className={styles.th}>Último movimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {datos?.proyectos.map((p) => <FilaProyecto key={p.id} p={p} />)}
                  {(datos?.proyectos.length ?? 0) === 0 && (
                    <tr>
                      <td className={styles.vacio} colSpan={6}>
                        No tiene proyectos. Entró, pero no llegó a crear ninguno.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <Text className={styles.small} style={{ display: 'block', marginBottom: '8px' }}>
              Lo que hizo, agrupado en ratos de trabajo. Un rato se corta con media hora de
              silencio. Lo más reciente va primero.
            </Text>
            {datos?.ratos.map((r) => (
              <div key={r.desde} className={styles.rato}>
                <div className={styles.ratoCabeza}>
                  <span className={styles.ratoCuando}>{fechaYHora(r.desde)}</span>
                  <span className={styles.small}>
                    {duracionLegible(r.duracionMin)} · {r.pasos.length}{' '}
                    {r.pasos.length === 1 ? 'acción' : 'acciones'}
                  </span>
                  {r.proyectos.length > 1 && (
                    <span className={styles.ratoSalto}>
                      saltó entre {r.proyectos.length} proyectos: {r.proyectos.join(', ')}
                    </span>
                  )}
                  {r.proyectos.length === 1 && (
                    <span className={styles.small}>en {r.proyectos[0]}</span>
                  )}
                </div>
                <ul className={styles.pasos}>
                  {r.pasos.map((p, i) => {
                    const det = detalleDelPaso(p.metadata);
                    return (
                      <li key={`${p.cuando}-${i}`} className={styles.paso}>
                        <span className={styles.pasoHora}>{soloHora(p.cuando)}</span>
                        <span>{p.queHizo}</span>
                        <span className={styles.pasoProyecto}>{p.proyecto ?? ''}</span>
                        {det && <span className={styles.detalle}>{det}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
            {(datos?.ratos.length ?? 0) === 0 && (
              <div className={styles.tablaWrap}>
                <div className={styles.vacio}>
                  No hizo nada en {dias === 365 ? 'el último año' : `los últimos ${dias} días`}.
                </div>
              </div>
            )}
          </>
        )}

        {datos?.huecoConocido && (
          <MessageBar intent="warning" style={{ marginTop: '14px' }}>
            <MessageBarBody>{datos.huecoConocido}</MessageBarBody>
          </MessageBar>
        )}
      </div>
    </div>
  );
}
